/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import atlasLogo from "@/assets/images/atlas-logo.png";
import googleIcon from "@/assets/images/google-icon.png";
import facebookIcon from "@/assets/images/facebook-icon.png";
import nextdoorIcon from "@/assets/images/nextdoor-icon.png";
import bbbBadge from "@/assets/images/bbb-badge.png";
import carfaxLogo from "@/assets/images/carfax-logo.jpeg";
import heroBackground from "@/assets/images/hero-background.jpg";
import technetLogo from "@/assets/images/technet-logo.png";
import route66Logo from "@/assets/images/route66-logo.png";
import neighborhoodFavesLogo from "@/assets/images/neighborhood-faves-logo.png";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo mappings
  "/images/atlas-logo.png": atlasLogo,
  "/assets/images/atlas-logo.png": atlasLogo,
  "//images.squarespace-cdn.com/content/v1/6644d38ed4adce7ffd20a16a/4fd5740b-3cc3-4531-9faf-8014303a402c/Untitled+%283%29.png": atlasLogo,
  
  // Social media icons
  "/images/google-icon.png": googleIcon,
  "/assets/images/google-icon.png": googleIcon,
  "//images.squarespace-cdn.com/content/v1/6644d38ed4adce7ffd20a16a/813ae006-6814-438c-8fcf-26ecf09d492b/google_g_icon_download.png": googleIcon,
  
  "/images/facebook-icon.png": facebookIcon,
  "/assets/images/facebook-icon.png": facebookIcon,
  "//images.squarespace-cdn.com/content/v1/6644d38ed4adce7ffd20a16a/38fb5bb9-71ee-4db9-be74-4994dbc31c90/Facebook_Logo_Primary.png": facebookIcon,
  
  "/images/nextdoor-icon.png": nextdoorIcon,
  "/assets/images/nextdoor-icon.png": nextdoorIcon,
  "//images.squarespace-cdn.com/content/v1/6644d38ed4adce7ffd20a16a/b0f3ea5c-8329-41b0-a89c-3c176d207844/NextdoorLogo_Lime.png": nextdoorIcon,
  
  // Review platform icons
  "/images/bbb-badge.png": bbbBadge,
  "/assets/images/bbb-badge.png": bbbBadge,
  "//seal-tulsa.bbb.org/customer-reviews/badge-10-bbb-38109987.png": bbbBadge,
  
  "/images/carfax-logo.jpeg": carfaxLogo,
  "/assets/images/carfax-logo.jpeg": carfaxLogo,
  "//images.squarespace-cdn.com/content/v1/6644d38ed4adce7ffd20a16a/a99a99ed-1e2f-47f9-a4b0-91cc1eaebda8/ideZUtbf1y_logos.jpeg": carfaxLogo,
  "//static.carfax.com/global-header/imgs/logo.svg": carfaxLogo,
  
  // Hero background image
  "/images/hero-background.jpg": heroBackground,
  "/assets/images/hero-background.jpg": heroBackground,
  "//images.squarespace-cdn.com/content/v1/6644d38ed4adce7ffd20a16a/e30b1f8a-4737-41ea-8445-e8a43c5b5250/PXL_20230508_140204878%7E2.jpg": heroBackground,

  // Footer certification logos
  "/images/technet-logo.png": technetLogo,
  "/assets/images/technet-logo.png": technetLogo,
  "//images.squarespace-cdn.com/content/v1/6644d38ed4adce7ffd20a16a/0fc8d99a-6077-428c-8e67-d88b77b1a020/TECHNET+LOGO+PNG.png": technetLogo,

  "/images/route66-logo.png": route66Logo,
  "/assets/images/route66-logo.png": route66Logo,
  "//images.squarespace-cdn.com/content/v1/6644d38ed4adce7ffd20a16a/baf79267-5d03-43c4-81ce-60f352f1c597/OK66%2BLogo+%281%29.png": route66Logo,

  "/images/neighborhood-faves-logo.png": neighborhoodFavesLogo,
  "/assets/images/neighborhood-faves-logo.png": neighborhoodFavesLogo,
  "//images.squarespace-cdn.com/content/v1/6644d38ed4adce7ffd20a16a/35d82d8b-74de-4600-b32f-cc0a94f9dd30/Neighborhood-Faves-2024-Digital-Kit_Sticker-copy-1.png": neighborhoodFavesLogo,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
